import { gsap } from "gsap";
import { Api } from "../../api/Api";
import { ResultCode } from "../../api/common/controller/net/constant/Constants";
import { BalanceEvent, BalanceManager } from "../../api/common/model/data/BalanceManager";
import { CommonOperatorEvent } from "../../api/common/operator/CommonOperatorEvent";
import { AppCore } from "../../api/core/AppCore";
import { AppParameters } from "../../api/core/AppParameters";
import { LoaderEvent } from "../../loader/event/LoaderEvent";
import { DeviceUtil } from "../../util/device/DeviceUtil";
import { EventHandler } from "../../util/event/EventHandler";
import { FreeRoundsUtils } from "../../util/freeRounds/FreeRoundsUtils";
import { Logger } from "../../util/log/Logger";
import { Delay } from "../../util/time/Delay";
import { AppData } from "../AppData";
import { AppEvent, GameErrorEvent } from "../Constants";
import { DialogEvent } from "../manager/DialogManager";
import { Renderer, ResizeEvent } from "../renderer/Renderer";
import { AbstractController } from "./AbstractController";
import { CommonGameEvent } from "./event/CommonGameEvent";
import { FreeRoundsGameManager } from "./FreeRoundsGameManager";
/**
 * WrapperController.ts
 */
export class WrapperController extends AbstractController {
    constructor(factory) {
        super();
        this.factory = factory;
        this.run();
    }
    setFactory(factory) {
        this.factory = factory;
        this.model = this.factory.getModel();
        this.addEventListeners();
    }
    run() {
        if (!this.running) {
            this.running = true;
            this.create();
            this.postCreate();
        }
    }
    updateBalance(balance) {
        throw new Error("Method not implemented.");
    }
    create() {
        this.createWrapper();
        this.createBalanceManager();
        this.wrapper.initialise(this);
    }
    postCreate() {
        super.postCreate();
        Logger.addObjectRef(this, "WrapperController");
    }
    addEventListeners() {
        this.registerCore(AppEvent.EXIT_GAME, this.sendExitGame);
        this.registerCore(AppEvent.DEPOSIT, this.sendDeposit);
        this.registerCore(AppEvent.BET_HISTORY, this.sendBetHistory);
        this.registerCore(AppEvent.BONUS, this.sendBonus);
        this.registerCore(AppEvent.SOUND_ON, this.sendSoundOn);
        this.registerCore(AppEvent.SOUND_OFF, this.sendSoundOff);
        this.registerCore(LoaderEvent.PROGRESS, this.handleProgressUpdate);
        this.registerCore(LoaderEvent.COMPLETE, this.handleLoaderComplete);
        this.registerCore(LoaderEvent.PRELOADER_COMPLETE, this.handlePreloaderComplete);
        this.registerCore(LoaderEvent.LOADER_REMOVE, this.sendLoaderRemove);
        this.registerCore(AppEvent.IDLE, this.handleIdle);
        this.registerCore(AppEvent.BUSY, this.handleBusy);
        this.registerGame(CommonGameEvent.START_GAME, this.handleStartGame);
        this.registerGame(CommonGameEvent.COMPLETE, this.handleComplete);
        this.registerGame(CommonGameEvent.FREE_ROUNDS_NEW_GAME_REQUEST, this.handleComplete);
        this.registerGame(CommonGameEvent.INITIALISE, this.handleInitialise);
        this.registerGame(CommonGameEvent.STAKE_UPDATED, this.handleStakeUpdated);
        this.registerGame(CommonGameEvent.RECOVERY, this.handleRecovery);
        this.registerGame(CommonGameEvent.PURCHASE_SPIN, this.handlePurchaseSpin);
        this.registerGame(CommonGameEvent.PURCHASE_ENTRY, this.handlePurchaseEntry);
        this.registerGame(CommonGameEvent.RESPONSIBLE_GAMING_INSTRUCTIONS_OPEN, this.handleResponsibleGamingOpen);
        this.registerGame(CommonGameEvent.RESPONSIBLE_GAMING_INSTRUCTIONS_CLOSE, this.handleResponsibleGamingClose);
        this.registerGame(CommonGameEvent.CURRENT_GAME_RESULT, this.currentGameResults);
        this.registerCore(DialogEvent.ACTIVE, this.gameDialogOpened);
        this.registerCore(DialogEvent.INACTIVE, this.gameDialogClosed);
        this.registerGame(CommonGameEvent.BACKGROUND_SOUND_ON, this.sendSoundOn);
        this.registerGame(CommonGameEvent.SFX_ON, this.sendSoundOn);
        this.registerCore(CommonGameEvent.SOUND_MUTED, this.sendSoundOff);
        this.registerCore(CommonGameEvent.DGH_OPEN, this.handleDGHOpen);
        this.registerGame(CommonGameEvent.BONUS, this.onBonus);
        this.registerGame(CommonGameEvent.BONUS_WON, this.onBonusWon);
        // error
        this.registerCore(AppEvent.ERROR, this.handleError);
        this.registerCore(GameErrorEvent.SESSION_TIME_OUT, this.handleSessionTimeout);
        this.registerCore(GameErrorEvent.INSUFFICIENT_FUNDS, this.handleInsufficientFunds);
        this.registerCore(GameErrorEvent.CURRENCY_NOT_SUPPORTED, this.handleCurrencyNotSupported);
        this.registerCore(GameErrorEvent.ACCOUNT_LIMIT_EXCEEDED, this.handleAccountLimitExceeded);
        this.registerCore(GameErrorEvent.RESPONSIBLE_GAMING_TIME_LIMIT, this.handleResponsibleGamingTimeLimit);
        this.registerCore(GameErrorEvent.REGULATORY_MESSAGE, this.handleRegulatoryMessage);
        this.registerCore(GameErrorEvent.UNKNOWN_PROMOTION_CODE, this.handleUnknownPromotionCode);
        this.registerCore(GameErrorEvent.USER_NOT_AUTHENTICATED, this.handleUserNotAuthenticated);
        this.registerCore(GameErrorEvent.GAME_ACCESS_DENIED, this.handleGameAccessDenied);
        this.registerCore(GameErrorEvent.CONNECTION_ERROR, this.handleConnectionError);
        this.registerCore(ResizeEvent.RESIZE, this.handleResizeEvent);
        this.registerCore(ResizeEvent.POST_ROTATE, this.handlePostRotate);
        // error
        this.registerCore(CommonOperatorEvent.OPERATOR_DATA, this.handleOperatorData);
        this.registerCore(CommonOperatorEvent.WRAPPER_OBJECT, this.handleWrapperObject);
        // analytics
        this.registerCore(CommonGameEvent.BI_ERROR, this.handleBiError);
        this.registerCore(CommonGameEvent.BI_KPI, this.handleBiKpi);
        this.registerCore(CommonGameEvent.BI_DEBUG, this.handleBiDebug);
    }
    onBonus() {
        this.model.dispatchKPI("WRAPPER_ACTION", "BONUS_ROUND_STARTED", null);
        this.wrapper.updateAction(so.GameAction.BONUS);
    }
    onBonusWon(response, onCollectResponse) {
        this.model.setHasBonusGame(true);
        this.model.dispatchKPI("WRAPPER_ACTION", "BONUS_ROUND_ENDED", null);
        this.wrapper.updateAction(so.GameAction.BONUS_WIN);
    }
    createModel() {
        this.model = this.factory.getModel();
    }
    createWrapper() {
        this.wrapper = so.Wrapper.getInstance();
    }
    createBalanceManager() {
        this.balanceManager = BalanceManager.getInstance();
        this.balanceManager.addEventListener(BalanceEvent.UPDATED, this.handleBalanceUpdated, this);
        this.balanceManager.addEventListener(BalanceEvent.WIN_PROCESSOR_RSP, this.handleWinProcessorRsp, this);
    }
    handleWinProcessorRsp() {
        let rsp = this.balanceManager.getWinProcessorRsp();
        let event;
        if (rsp == ResultCode.RETRIABLE_WIN_OCCURRED) {
            event = AppEvent.ERROR;
        }
        else if (rsp === ResultCode.GAME_ACCESS_DENIED) {
            event = GameErrorEvent.GAME_ACCESS_DENIED;
        }
        else if (rsp === ResultCode.USER_NOT_AUTHENTICATED) {
            event = GameErrorEvent.USER_NOT_AUTHENTICATED;
        }
        else if (rsp === ResultCode.UNKNOWN_PROMOTION_CODE) {
            event = GameErrorEvent.UNKNOWN_PROMOTION_CODE;
        }
        else {
            this.handleRegulatoryMessage();
        }
        if (event) {
            this.dispatchCore(event);
        }
    }
    error(type, data) {
        Logger.forceLog("Error in Wrapper Controller: " + type.getId());
        console.log(data);
        if (type.is(so.ErrorType.REALITY_CHECK)) {
            this.handleRealityCheck(data);
        }
        else if (type.is(so.ErrorType.WAGER_BONUS_MESSAGE)) {
            this.dispatchCore(GameErrorEvent.WAGER_BONUS);
        }
        else if (type.is(so.ErrorType.WAGER_CASH_MESSAGE)) {
            this.dispatchCore(GameErrorEvent.WAGER_CASH);
        }
    }
    updateAction(action, data) {
        Logger.forceLog("Update Action in Wrapper Controller");
        console.log(action);
        this.handleWrapperAction(action, data);
    }
    updateFreeRounds() {
    }
    updateGameState(state) {
        this.wrapper.updateState(state);
    }
    handleWrapperAction(action, data) {
        console.log("handle Wrapper Action in titan-game");
        console.log(action);
        if (action instanceof so.WrapperFreeRoundsAction) {
            console.log("> Instance of FR wrapper action");
            if (this.freeRoundsGameManager == null) {
                this.freeRoundsGameManager = new FreeRoundsGameManager(this.factory, this.model, this.wrapper);
            }
            this.freeRoundsGameManager.handleWrapperAction(action, data);
        }
        else if (action instanceof so.WrapperAction) {
            console.log("> Instance of wrapper action");
            if (action.isResize()) {
                this.handleResizeWrapperAction();
            }
            else if (action.isSoundOn()) {
                this.handleSoundOn();
            }
            else if (action.isSoundOff()) {
                this.handleSoundOff();
            }
            else if (action.isHelpOpen()) {
                this.handleHelpOpen();
            }
            else if (action.isHelpClose()) {
                this.handleHelpClose();
            }
            else if (action.isResume()) {
                this.handleRenderStart();
            }
            else if (action.isPause()) {
                this.handleRenderStop();
            }
            else if (action.isShowSignposting()) {
                this.addSignposting();
            }
            else if (action.isHideSignposting()) {
                this.removeSignposting();
            }
            else if (action.isBalanceRefresh()) {
                Api.getBalance(this.handleGetBalanceResponse, this, true);
            }
            else if (action.isErrorComplete()) {
                this.handleErrorComplete();
            }
            else if (action.isAutoPlayStop()) {
                this.handleAutoPlayStop();
            }
            else if (action.isNextGameRequest()) {
                this.handleNextGameRequest();
            }
            else if (action.isGameReplay()) {
                this.handleGameReplay(data);
            }
            else if (action.isSetGameOffset()) {
                let renderer = Renderer.getInstance();
                let wrapperConfig = this.getWrapperConfig();
                let operatorConfig = wrapperConfig.getOperatorConfig();
                let verticalOffset = operatorConfig.getVerticalOffset();
                if (verticalOffset != 0) {
                    renderer.setVerticalOffSet(verticalOffset);
                    renderer.forceResize();
                }
            }
        }
    }
    handleFreeRoundsIn(data) {
        console.log("Free Rounds: Request to enable free rounds");
        const frData = this.model.getFreeRoundsData();
        frData.setUUID(data.uuid);
        frData.setName(data.name);
        frData.setDescription(data.description);
        frData.setID(data.id);
        frData.setRounds(data.rounds);
        frData.setRoundsRemaining(data.roundsRemaining);
        frData.setSiteID(data.siteID);
        frData.setStake(data.stake);
        frData.setExpiryDate(data.expiryDate);
        frData.setFreeRoundsEnabled(true);
        frData.setCampaignAccumulatedWins(data.campaignAccumulatedWins);
        this.dispatchGame(CommonGameEvent.FREE_ROUNDS_IN);
    }
    handleFreeRoundsOut() {
        console.log("Free Rounds: Request to disable free rounds");
        this.model.getFreeRoundsData().setFreeRoundsEnabled(false);
        this.dispatchGame(CommonGameEvent.FREE_ROUNDS_OUT);
    }
    handleFreeExtraSpins() {
        console.log("Free Rounds: Request to switch to extra spins");
        this.dispatchGame(CommonGameEvent.FREE_ROUNDS_EXTRA_SPINS);
    }
    handleFreeRoundsAvailable() {
        console.log("Free Rounds: Free Rounds available");
        this.model.setFreeRoundsAvailable(true);
        this.updateFreeRoundsPosition();
    }
    handleFreeRoundsUnavailable() {
        console.log("Free Rounds: Free Rounds unavailable");
        this.model.setFreeRoundsAvailable(false);
    }
    handleGetBalanceResponse(value) {
        const balance = value.getBalances()[0];
        this.model.setBalance(balance);
        this.dispatchCore(CommonGameEvent.BALANCE_UPDATED);
        console.log("Balance Response in WC");
    }
    wrapperFailed(config, error) {
        Logger.forceLog("Wrapper Failed");
        console.log(error);
        this.config = config;
        console.log("Wrapper Config");
        console.log(config);
        this.wrapper.error(error);
        this.model.getErrorData().setCode(error.getCode());
        this.failedHandler.call();
    }
    wrapperReady(config) {
        Logger.forceLog("Wrapper Ready");
        console.log(config);
        this.config = config;
        this.setAppParams();
        this.readyHandler.call();
    }
    currentGameResults() {
        console.log("Game results");
        this.handleFreeRoundsOut();
    }
    onWrapperReady(handler, scope) {
        this.readyHandler = new EventHandler(handler, scope);
    }
    onWrapperFailed(handler, scope) {
        this.failedHandler = new EventHandler(handler, scope);
    }
    setAppParams() {
        let operatorParameters = this.config.getOperatorParameters();
        let gameServer = operatorParameters.getGameServer();
        let token = this.config.getLogin().getToken();
        let appParams = new AppParameters(gameServer, DeviceUtil.getDeviceType());
        appParams.setAccessToken(token);
        AppCore.getInstance().initialise(appParams);
    }
    getWrapperConfig() {
        return this.config;
    }
    registerGame(event, action) {
        this.model.register(event, action, this);
    }
    sendExitGame() {
        this.wrapper.updateAction(so.GameAction.EXIT_GAME);
    }
    sendDeposit() {
        this.model.dispatchKPI("WRAPPER_ACTION", "DEPOSIT_BUTTON_PRESSED", null);
        this.wrapper.updateAction(so.GameAction.DEPOSIT);
    }
    sendBetHistory() {
        this.model.dispatchKPI("WRAPPER_ACTION", "BET_HISTORY", null);
        this.wrapper.updateAction(so.GameAction.BET_HISTORY);
    }
    sendBonus() {
        this.wrapper.updateAction(so.GameAction.BONUS);
    }
    sendSoundOn() {
        console.log("sound on in WC");
        this.wrapper.updateAction(so.GameAction.SOUND_ON);
    }
    sendSoundOff() {
        console.log("sound off in WC");
        this.wrapper.updateAction(so.GameAction.SOUND_OFF);
    }
    sendLoaderRemove() {
        this.model.dispatchKPI("WRAPPER_ACTION", "GAME_LOADING_PAGE_PLAY_BUTTON_PRESSED", null);
        this.wrapper.updateAction(so.GameAction.LOADER_REMOVE);
    }
    handleResizeWrapperAction() {
        Renderer.getInstance().forceResize();
    }
    gameDialogOpened() {
        console.log("Game dialog opened");
        this.wrapper.updateAction(so.GameAction.GAME_DIALOG_OPENED);
    }
    gameDialogClosed() {
        console.log("Game dialog closed");
        this.wrapper.updateAction(so.GameAction.GAME_DIALOG_CLOSED);
    }
    handleResizeEvent() {
        this.updateFreeRoundsPosition();
        this.forceRefreshReplayMenuBackground();
    }
    handlePostRotate() {
        this.forceRefreshReplayMenuBackground();
    }
    forceRefreshReplayMenuBackground() {
        if (this.replayMenuOpen && gsap.globalTimeline.timeScale() === 0) {
            //advance the ticker to refresh the page
            if (this.replayUpdateTween instanceof gsap.core.Tween) {
                this.replayUpdateTween.kill();
                this.replayUpdateTween = null;
            }
            gsap.globalTimeline.timeScale(1);
            this.replayUpdateTween = gsap.delayedCall(1, () => {
                if (this.replayMenuOpen) {
                    gsap.globalTimeline.timeScale(0);
                }
            });
        }
    }
    updateFreeRoundsPosition() {
        if (this.model.isFreeRoundsAvailable()) {
            const frUpdate = FreeRoundsUtils.getInstance().updateProperties(this.factory.getFreeRoundsUIProperties());
            this.wrapper.updateFreeRoundsPositions(frUpdate);
        }
    }
    handleSoundOn() {
        this.dispatchCore(CommonOperatorEvent.SOUND_ON);
    }
    handleSoundOff() {
        this.dispatchCore(CommonOperatorEvent.SOUND_OFF);
    }
    handleHelpOpen() {
        this.dispatchCore(CommonOperatorEvent.HELP_OPEN);
    }
    handleHelpClose() {
        this.dispatchCore(CommonOperatorEvent.HELP_CLOSE);
    }
    handleClientUpdated() {
        // let client: IWrapper = this.model.getClient();
        // this.wrapper.updateWrapper(client.getClient());
    }
    handleRenderStart() {
        Logger.debug("Render Start");
        if (gsap.globalTimeline.timeScale() === 0) {
            console.log("Ticker stopped, starting");
            gsap.globalTimeline.timeScale(1);
        }
        //GD-8143 - enable overflow while replay menu is open
        if (this.replayMenuOpen) {
            this.replayMenuOpen = false;
            document.body.style.overflow = "hidden";
        }
    }
    handleRenderStop() {
        Logger.debug("Render Stop");
        if (gsap.globalTimeline.timeScale() > 0) {
            console.log("Ticker started, stopping");
            gsap.globalTimeline.timeScale(0);
        }
    }
    handleErrorComplete() {
        console.log("handleErrorComplete in Wrapper Controller");
        let errorData = this.model.getErrorData();
        if (errorData.getCode() === ResultCode.INSUFFICIENT_FUNDS || errorData.getCode() === ResultCode.INSUFFICIENT_BALANCE ||
            errorData.getCode() === ResultCode.OPERATOR_ERROR || errorData.getCode() === ResultCode.REGULATORY_MESSAGE
            || errorData.getCode() === ResultCode.ACCOUNT_LIMIT_EXCEEDED || errorData.getCode() === ResultCode.ACCOUNT_LIMIT_EXCEEDED_GENERIC
            || errorData.getCode() === ResultCode.RESPONSIBLE_GAMING_TIME_LIMIT_MESSAGE) {
            errorData.reset();
            this.dispatchCore(AppEvent.ERROR_COMPLETE);
        }
    }
    handleAutoPlayStop() {
        this.dispatchCore(CommonOperatorEvent.STOP_AUTO_SPINS);
    }
    handleNextGameRequest() {
        console.log("Next game request");
        this.dispatchGame(CommonGameEvent.NEXT_GAME_REQUEST);
    }
    handleLoaderComplete() {
        this.model.dispatchKPI("TITAN_STATE", "GAME_LOADING_PAGE_STARTED", null);
        this.updateGameState(so.GameState.LOAD_COMPLETE);
    }
    handlePreloaderComplete() {
        this.model.dispatchKPI("TITAN_STATE", "BRAND_LOADING_PAGE_COMPLETED", null);
        this.wrapper.updateAction(so.GameAction.LOADER_SHOW);
        this.updateGameState(so.GameState.PRELOAD_COMPLETE);
    }
    handleIdle() {
        this.updateGameState(so.GameState.GAME_IDLE);
    }
    handleBusy() {
        this.updateGameState(so.GameState.GAME_BUSY);
    }
    handleStartGame() {
        this.updateGameState(so.GameState.START_GAME);
    }
    handleComplete() {
        this.updateWin();
        this.updateGameState(so.GameState.GAME_COMPLETE);
        this.model.getFreeRoundsData().setUUID("");
        this.dispatchGame(CommonGameEvent.FREE_ROUNDS_OUT);
    }
    handleInitialise() {
        this.model.dispatchKPI("WRAPPER_STATE", "GAME_SESSION_READY", null);
        this.updateGameState(so.GameState.GAME_READY);
    }
    handleStakeUpdated() {
        this.updateStake();
    }
    handleRecovery() {
        this.updateStake();
        this.model.dispatchKPI("WRAPPER_STATE", "GAME_SESSION_RECOVERY_STARTED", null);
        this.updateGameState(so.GameState.RECOVERY);
    }
    handlePurchaseEntry() {
        this.updateGameState(so.GameState.PURCHASE_ENTRY);
    }
    handlePurchaseSpin() {
        this.updateGameState(so.GameState.PURCHASE_SPIN);
    }
    handleResponsibleGamingOpen() {
        console.log("Responsible gaming open");
        this.wrapper.updateAction(so.GameAction.RESPONSIBLE_GAMING_OPEN);
    }
    handleResponsibleGamingClose() {
        console.log("Responsible gaming close");
        this.wrapper.updateAction(so.GameAction.RESPONSIBLE_GAMING_CLOSE);
    }
    handleOperatorData() {
        let errorData = this.core.getOperatorData();
        let details = errorData.getDetails();
        this.wrapper.updateOperatorData(details.toObject());
    }
    handleWrapperObject() {
        let wrapperObject = this.core.getWrapperObject();
        this.wrapper.updateWrapper(wrapperObject);
    }
    handleGameReplay(data) {
        this.model.setGameReplay(data);
        this.dispatchGame(CommonGameEvent.GAME_REPLAY);
    }
    handleBiError(event) {
        const { category, operation, value, options } = event.getPayload();
        this.wrapper.updateAnalyticsError({ category: category, operation: operation, value: value });
    }
    handleBiKpi(event) {
        const { category, operation, value, options } = event.getPayload();
        this.wrapper.updateAnalyticsKpi({ category: category, operation: operation, value: value });
    }
    handleBiDebug(event) {
        const { category, operation, value, options } = event.getPayload();
        this.wrapper.updateAnalyticsDebug({ category: category, operation: operation, value: value });
    }
    addSignposting() {
        this.createDialogManager();
        this.dialogManager.addSignposting();
    }
    removeSignposting() {
        this.createDialogManager();
        this.dialogManager.removeSignposting();
    }
    createDialogManager() {
        if (!this.dialogManager) {
            this.dialogManager = this.factory.getDialogManager();
        }
    }
    handleBalanceUpdated() {
        const balance = this.model.getBalance();
        let cash = new so.Balance(so.BalanceType.CASH, balance.getCash());
        let bonus = new so.Balance(so.BalanceType.BONUS, balance.getBonus());
        let balances = new so.Balances(cash, bonus);
        this.wrapper.updateBalance(balances);
    }
    updateWin() {
        let win = this.balanceManager.getTotalWin();
        this.wrapper.updateWin(win);
    }
    updateStake() {
        let stake = this.model.getCurrentStake();
        this.wrapper.updateStake(stake);
    }
    handleProgressUpdate() {
        let progress = this.model.getLoaderProgress();
        this.wrapper.updateProgress(progress);
    }
    // error
    handleError() {
        this.wrapper.error(so.ErrorType.GENERIC_ERROR);
    }
    handleInsufficientFunds() {
        this.wrapper.error(so.ErrorType.INSUFFICIENT_FUNDS);
    }
    handleAccountLimitExceeded() {
        this.wrapper.error(so.ErrorType.ACCOUNT_LIMIT_EXCEEDED);
    }
    handleRegulatoryMessage() {
        this.wrapper.error(so.ErrorType.REGULATORY_MESSAGE);
    }
    handleResponsibleGamingTimeLimit() {
        this.wrapper.error(so.ErrorType.RESPONSIBLE_GAMING_TIME_LIMIT);
    }
    handleSessionTimeout() {
        this.wrapper.error(so.ErrorType.SESSION_EXPIRED);
    }
    handleCurrencyNotSupported() {
        this.wrapper.error(so.ErrorType.CURRENCY_NOT_SUPPORTED);
    }
    handleUnknownPromotionCode() {
        this.wrapper.error(so.ErrorType.CURRENCY_NOT_SUPPORTED);
    }
    handleUserNotAuthenticated() {
        this.wrapper.error(so.ErrorType.USER_NOT_AUTHENTICATED);
    }
    handleGameAccessDenied() {
        this.wrapper.error(so.ErrorType.GAME_ACCESS_DENIED);
    }
    handleConnectionError() {
        this.wrapper.error(so.ErrorType.CONNECTION_ERROR);
    }
    handleRealityCheck(data) {
        this.model.setRealityCheckDetails(data.getDetails());
        if (data.hasWinLoss()) {
            this.model.setWinLoss(data.getWinLoss());
        }
        this.dispatchCore(GameErrorEvent.REALITY_CHECK);
    }
    handleDGHOpen() {
        if (this.factory.hasInGameReplay()) {
            this.model.dispatchKPI("WRAPPER_ACTION", "GAME_REPLAY_OPENED", null);
            this.wrapper.updateAction(so.GameAction.DGH);
            //GD-8143 - replay menu must be allowed to scroll while it is open.
            this.replayMenuOpen = true;
            document.body.style.overflow = "auto";
        }
    }
    isNative() {
        return AppData.DEPLOY_TYPE.isNative() /* || AppData.DEPLOY_TYPE.isNativeLite()*/;
    }
    switchGameSuccess(token) {
        console.log("Wrapper Controller: Switch Game Success - " + token);
    }
    dispatchGame(event, delay = 0) {
        if (delay > 0) {
            Delay.create(delay, this.model.dispatch, this.model, event);
        }
        else {
            this.model.dispatch(event);
        }
    }
    startGameReplayFromId(id) {
        this.wrapper?.startGameReplayFromId(id);
    }
    getCertifiedGameClientVersion() {
        return AppData.RELEASE_VERSION;
    }
}
//# sourceMappingURL=WrapperController.js.map